<template>
  <Dialog v-model:visible="open" modal id="modal-content" class="overflow-y-hidden">
    <div class="grow flex flex-col overflow-y-hidden" ref="contentDiv" v-html="content"></div>

    <!-- Nested Modal -->
    <Dialog v-model:visible="open2" modal>
      <div ref="contentDiv2" v-html="content2"></div>
    </Dialog>

  </Dialog>
</template>

<script setup>
  import { ref, onMounted, nextTick, createApp } from 'vue'
  import PrimeVue from 'primevue/config'
  import Dialog from 'primevue/dialog'

  import DateTimePicker from "./DateTimePicker.vue"
  import DateTimeRangePicker from "./DateTimeRangePicker.vue"
  import InlineHelp from "./InlineHelp.vue"
  import Multiselect from "./Multiselect.vue"
  import UsersMultiselect from "./UsersMultiselect.vue"
  import { i18n } from '../entrypoints/i18n'

  import AlgorithmForm from "./AlgorithmForm.vue"

  import { formDependenciesPlugin } from '../dependencies/formDependencies'

  import MorePopover from './MorePopover.vue'
  import StarRating from './StarRating.vue'
  import GroupedMultiselect from './GroupedMultiselect.vue'

  import ToggleSwitch from './ToggleSwitch.vue'
  import PrivacyToggleSwitch from './PrivacyToggleSwitch.vue'

  import Tabs from 'primevue/tabs'
  import TabList from 'primevue/tablist'
  import Tab from 'primevue/tab'
  import TabPanels from 'primevue/tabpanels'
  import TabPanel from 'primevue/tabpanel'

  const open = ref(false)
  const open2 = ref(false)
  const content = ref('')
  const content2 = ref('')
  const contentDiv = ref(null)
  const contentDiv2 = ref(null)

  const hydrate = (elem) => {
    if (!elem) return

    const modalApp = createApp({ template: elem.innerHTML })

    modalApp
      .component('datetimepicker', DateTimePicker)
      .component('datetimerangepicker', DateTimeRangePicker)
      .component('tabs', Tabs)
      .component('tablist', TabList)
      .component('tab', Tab)
      .component('tabpanels', TabPanels)
      .component('tabpanel', TabPanel)
      .component('inlinehelp', InlineHelp)
      .component('multiselect', Multiselect)
      .component('usersmultiselect', UsersMultiselect)
      .component('algorithmform', AlgorithmForm)
      .component('morepopover', MorePopover)
      .component('starrating', StarRating)
      .component('groupedmultiselect', GroupedMultiselect)
      .component('toggleswitch', ToggleSwitch)
      .component('privacytoggleswitch', PrivacyToggleSwitch)

    modalApp.use(PrimeVue, { unstyled: true })
    modalApp.use(i18n)
    modalApp.use(formDependenciesPlugin)
    modalApp.mount(elem)

    // Select all elements with data-pre-controller
    const elements_to_hydrate = elem.querySelectorAll('[data-pre-hydrate-controller]')

    // Loop through each element and update the attribute
    elements_to_hydrate.forEach(element => {
      const value = element.getAttribute('data-pre-hydrate-controller')
      element.setAttribute('data-controller', value)
      element.removeAttribute('data-pre-hydrate-controller')
    })

    // Reinitialize reCAPTCHA after inserting HTML
    if (typeof grecaptcha !== 'undefined') {
      const metaTag = document.querySelector('meta[name="recaptcha-site-key"]');
      const recaptchaSiteKey = metaTag ? metaTag.content : null;

      if (recaptchaSiteKey && document.querySelector('.g-recaptcha')) {
        grecaptcha.render(document.querySelector('.g-recaptcha'), {
          sitekey: recaptchaSiteKey
        });
      }
    }

    // const submits = elem.querySelectorAll('.submit-button-container input.submit')

    // submits.forEach(element => {
    //   element.setAttribute("data-action", 'modal--close#delayedClose')
    // })

    const closestModal = elem.closest('[data-pc-name="dialog"]')

    const headerActionsContainer = closestModal.querySelector('[data-pc-section="headeractions"]')
    const header = elem.querySelector('.header-wrapper') || elem.getElementsByTagName('header')[0]
    const headerActions = closestModal.querySelector('[data-pc-section="headeractions"]') // must go here for the draggable to work

    if (header && headerActions) {
      headerActions.prepend(header)

      // border-b border-gray-200 not all modal headers will have a border bottom
      headerActionsContainer.classList.add('flex', 'justify-between', 'items-center', 'px-6', 'pb-4')

      /*
        Example of DOM:

        <div data-pc-section="header">
          <div data-pc-section="headeractions">
            <header>Branson Math</header>
            <button>x</button>
          </div>
        </div>
      */
    }
  }

  const camelToSnakeCase = str => str.replace(/[A-Z]/g, letter => `_${letter.toLowerCase()}`);

  const wrapFormContent = (raw_content, data = {}, containerClass = 'form-content-container') => {
    let parser = new DOMParser();
    let doc = parser.parseFromString(raw_content, 'text/html');
    let form = doc.querySelector('form');

    if (form) {
      const container = document.createElement('div');
      container.className = containerClass;

      const children = Array.from(form.children);

      children.forEach(child => {
        if (!child.classList.contains('submit-button-container')) {
          container.appendChild(child);
        }
      });

      form.insertBefore(container, form.firstChild);

      // Append special fields to make it easier to pass data from the original link
      // to the form action URL
      const allowedDataKeys = ['originItemId', 'publicProgramId', 'parentPath']
      Object.keys(data).forEach(key => {
        if (allowedDataKeys.includes(key)) {
          const input = document.createElement('input')
          input.type = 'hidden'
          input.value = data[key]
          input.name = camelToSnakeCase(key)
          form.append(input)
        }
      })
    }
    return doc.body.innerHTML;
  }

  const open_url = async (url, data = {}) => {
    // Convert data object to URL search params
    const urlObj = new URL(url, window.location.origin)
    Object.keys(data).forEach(key => {
      if (data[key] !== undefined && data[key] !== null) {
        urlObj.searchParams.set(key, data[key])
      }
    })

    const response = await fetch(urlObj.toString(), {
      headers: { 'X-Requested-With': 'XMLHttpRequest' }
    })

    const contentType = response.headers.get('content-type')
    if (contentType && contentType.includes('application/json')) {
      const data = await response.json()
      if (data.redirect) {
        window.location.href = data.redirect
        return
      }
    }

    let raw_content = await response.text()
    raw_content = raw_content.replaceAll("data-controller", "data-pre-hydrate-controller")

    if (open.value) { // Open the second modal
      open2.value = true
      content2.value = wrapFormContent(raw_content, data) // modified_content

      nextTick(() => {
        hydrate(contentDiv2.value)
      })
    } else { // Open the first modal
      open.value = true
      content.value = wrapFormContent(raw_content, data) // modified_content

      nextTick(() => {
        hydrate(contentDiv.value)
      })
    }
  }

  onMounted(() => {
    const close_top_modal = (event) => {
      if (event.type === 'keydown' && event.key === 'Escape') {
        event.preventDefault()
        event.stopImmediatePropagation() // Stop the event from affecting both modals

        if (open2.value) {
          open2.value = false
        } else if (open.value) {
          open.value = false
        }
      }

      if (event.type === 'close-top-modal' || event.type === 'click') {
        if (open2.value) {
          open2.value = false
        } else {
          open.value = false
        }
      }
    }

    document.addEventListener('keydown', close_top_modal)
    document.addEventListener('close-top-modal', close_top_modal)

    return () => {
      document.removeEventListener('keydown', close_top_modal)
      document.removeEventListener('close-top-modal', close_top_modal)
    }
  })

  defineExpose({
    open_url
  })
</script>
